import {useMutation, useQuery} from '@apollo/client'
import React, {useEffect, useState} from 'react'
import {Button, Col, FloatingLabel, Form, Offcanvas, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {IResAddAppointment} from '../../leads/leadTypes'
import {CREATE_UPDATE_APPOINTMENT} from '../../../gql/mutations/leadMutations'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {NodeAppointmentSet} from '../../leads/components/sectionsTopNav/sectionNavTypes'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {IResAllListUsers} from '../../../types/users'
import {DateInput} from '../../../components/DateInput'
import moment from 'moment'
import {useGetUser} from '../../../store/userStore'

interface IAppointsProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  leadId: string
  infoEdit: Omit<NodeAppointmentSet, 'created'> | null
}

const requiredMark = '*'

const aM = defineMessages({
  title: {id: 'leads.appointment.title', defaultMessage: 'Appointment'},
  type: {id: 'leads.appointment.type', defaultMessage: 'Appointment type'},
  meetingLocation: {id: 'leads.appointment.meetingLocation', defaultMessage: 'Meeting location'},
  datePlaceholder: {id: 'leads.appointment.datePlaceholder', defaultMessage: 'Appointment date'},
  time: {id: 'leads.appointment.time', defaultMessage: 'Appointment time'},
  duration: {id: 'leads.appointment.duration', defaultMessage: 'Duration (minutes)'},
  creditTo: {id: 'leads.appointment.creditTo', defaultMessage: 'Credit to'},
  commentPlaceholder: {id: 'leads.appointment.commentPlaceholder', defaultMessage: 'Insert comment'},
  toastAdded: {id: 'leads.appointment.toast.added', defaultMessage: 'Appointment added successfully'},
  toastEdited: {id: 'leads.appointment.toast.edited', defaultMessage: 'Appointment edited successfully'},
})

export const AddEditAppointmentDrawer: React.FC<IAppointsProps> = ({
  isOpen,
  onClose,
  refetch,
  leadId,
  infoEdit,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlCreate, {data, loading, error}] =
    useMutation<IResAddAppointment>(CREATE_UPDATE_APPOINTMENT)
  const hasDateTime = infoEdit?.date
    ? infoEdit.date.includes('T') || infoEdit.date.includes(' ')
    : false
  const [appointmentDate, setAppointmentDate] = useState<string>(
    infoEdit?.date
      ? hasDateTime
        ? moment.utc(infoEdit.date).local().format('YYYY-MM-DD')
        : moment(infoEdit.date).format('YYYY-MM-DD')
      : ''
  )
  const [appointmentTime, setAppointmentTime] = useState<string>(
    infoEdit?.date && moment(infoEdit.date).isValid()
      ? hasDateTime
        ? moment.utc(infoEdit.date).local().format('HH:mm')
        : '00:00'
      : ''
  )

  const {
    data: dataMembers,
    error: errorMembers,
    loading: loadingMembers,
  } = useQuery<IResAllListUsers>(GET_ALL_USERS_LIST, {fetchPolicy: 'no-cache'})

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
  }, [error])
  useEffect(() => {
    if (errorMembers) toast.error(errMsg(errorMembers.message))
  }, [errorMembers])
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id ? parseId(currentUser.id) : ''
  const hasUserOption = (id: string | number) =>
    !!id && dataMembers?.allUsersList?.some((node) => String(parseId(node.id)) === String(id))
  const defaultAssignedTo = infoEdit?.assignedTo?.id
    ? parseId(infoEdit.assignedTo.id)
    : hasUserOption(currentUserId)
    ? currentUserId
    : ''
  const defaultCreditTo = infoEdit?.creditTo?.id
    ? parseId(infoEdit.creditTo.id)
    : hasUserOption(currentUserId)
    ? currentUserId
    : ''

  useEffect(() => {
    if (infoEdit?.date) {
      const hasDateTimeValue = infoEdit.date.includes('T') || infoEdit.date.includes(' ')
      if (hasDateTimeValue) {
        const local = moment.utc(infoEdit.date).local()
        setAppointmentDate(local.format('YYYY-MM-DD'))
        if (local.isValid()) setAppointmentTime(local.format('HH:mm'))
      } else {
        setAppointmentDate(moment(infoEdit.date).format('YYYY-MM-DD'))
        setAppointmentTime('00:00')
      }
    }
  }, [infoEdit?.date])

  useEffect(() => {
    if (!data) return
    if (data.appointmentCreateupdateMutation.appointment?.id) {
      toast.success(intl.formatMessage(infoEdit ? aM.toastEdited : aM.toastAdded))
      refetch()
      onClose()
    } else if (data.appointmentCreateupdateMutation?.errors?.length > 0) {
      toast.error(errMsg(data.appointmentCreateupdateMutation.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {
      tittle,
      typeAppointment,
      meetingLocation,
      appointmentTime,
      duration,
      assignedTo,
      creditTo,
      comment,
    } = Object.fromEntries(new FormData(e.currentTarget))
    const dateValue =
      appointmentDate && appointmentTime
        ? moment(`${appointmentDate} ${appointmentTime}`, 'YYYY-MM-DD HH:mm')
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss[Z]')
        : appointmentDate
    gqlCreate({
      variables: {
        input: {
          tittle,
          typeAppointment,
          meetingLocation,
          date: dateValue,
          duration,
          assignedTo,
          creditTo,
          comment,
          lead: leadId,
          id: infoEdit && infoEdit.id ? parseId(infoEdit.id) : undefined,
        },
      },
    })
  }
  return (
    <Offcanvas show={isOpen} onHide={onClose} placement='end' className='w-100 w-md-50 w-lg-50'>
      <Offcanvas.Header closeButton className='bg-light'>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <Offcanvas.Title className='d-flex align-items-center gap-4'>{intl.formatMessage(aM.title)}</Offcanvas.Title>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {loadingMembers || !dataMembers ? (
          <div>
            <Spinner animation='border' />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className='w-100 h-100'>
              <div>
                <Row>
                  <Col className='col-12 mb-4'>
                    <FloatingLabel
                      controlId='name'
                      label={
                        <span>
                          <FormattedMessage id='common.field.title' defaultMessage='Title' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                      className='mb-3'
                    >
                      <Form.Control
                        placeholder={intl.formatMessage({id: 'common.field.title', defaultMessage: 'Title'})}
                        name='tittle'
                        required
                        defaultValue={infoEdit?.tittle || undefined}
                      />
                    </FloatingLabel>
                  </Col>
                  {/* two */}
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <FloatingLabel
                      controlId='typeAppointment'
                      label={
                        <span>
                          <FormattedMessage id='leads.appointment.type' defaultMessage='Appointment type' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                    >
                      <Form.Control
                        placeholder={intl.formatMessage(aM.type)}
                        name='typeAppointment'
                        required
                        defaultValue={infoEdit?.typeAppointment || undefined}
                      />
                    </FloatingLabel>
                  </Col>
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <FloatingLabel
                      controlId='meetingLocation'
                      label={
                        <span>
                          <FormattedMessage id='leads.appointment.meetingLocation' defaultMessage='Meeting location' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                      className='mb-3'
                    >
                      <Form.Control
                        placeholder={intl.formatMessage(aM.meetingLocation)}
                        name='meetingLocation'
                        required
                        defaultValue={infoEdit?.meetingLocation || undefined}
                      />
                    </FloatingLabel>
                  </Col>
                  {/* two */}
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <DateInput
                      id='date'
                      name='date'
                      label={
                        <>
                          <FormattedMessage id='leads.appointment.when' defaultMessage='When is the appointment' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </>
                      }
                      placeholder={intl.formatMessage(aM.datePlaceholder)}
                      value={appointmentDate}
                      onChange={(_, value) => setAppointmentDate(value || '')}
                      required
                    />
                  </Col>
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <Form.Group>
                      <Form.Label>
                        <FormattedMessage id='leads.appointment.time' defaultMessage='Appointment time' /> <span style={{color: 'red'}}>{requiredMark}</span>
                      </Form.Label>
                      <Form.Control
                        type='time'
                        name='appointmentTime'
                        required
                        value={appointmentTime}
                        onChange={(e) => setAppointmentTime(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <FloatingLabel
                      controlId='duration'
                      label={
                        <span>
                          <FormattedMessage id='leads.appointment.duration' defaultMessage='Duration (minutes)' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                      className='mb-3'
                    >
                      <Form.Control
                        placeholder={intl.formatMessage(aM.duration)}
                        name='duration'
                        type='number'
                        required
                        min={1}
                        defaultValue={infoEdit?.duration || undefined}
                      />
                    </FloatingLabel>
                  </Col>
                  {/* two */}
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <FloatingLabel
                      controlId='assignedTo'
                      label={
                        <span>
                          <FormattedMessage id='leads.list.summary.assignedTo' defaultMessage='Assigned to' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                    >
                      <Form.Select
                        placeholder={intl.formatMessage({id: 'leads.list.summary.assignedTo', defaultMessage: 'Assigned to'})}
                        name='assignedTo'
                        required
                        defaultValue={defaultAssignedTo}
                        disabled={loadingMembers}
                      >
                        <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                        {dataMembers?.allUsersList.map((node) => (
                          <option key={node.id} value={parseId(node.id)}>
                            {node.firstName} {node.lastName}
                          </option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                    <FloatingLabel
                      controlId='creditTo'
                      label={
                        <span>
                          <FormattedMessage id='leads.appointment.creditTo' defaultMessage='Credit to' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                    >
                      <Form.Select
                        placeholder={intl.formatMessage(aM.creditTo)}
                        name='creditTo'
                        required
                        defaultValue={defaultCreditTo}
                        disabled={loadingMembers}
                      >
                        <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
                        {dataMembers?.allUsersList.map((node) => (
                          <option key={node.id} value={parseId(node.id)}>
                            {node.firstName} {node.lastName}
                          </option>
                        ))}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  {/* ONE */}
                  <Col className='col-12 mb-4'>
                    <FloatingLabel controlId='comment' label={intl.formatMessage({id: 'common.field.comment', defaultMessage: 'Comment'})} className='mb-3'>
                      <Form.Control
                        placeholder={intl.formatMessage(aM.commentPlaceholder)}
                        as='textarea'
                        rows={4}
                        type='textarea'
                        name='comment'
                        className='h-100'
                        required
                        defaultValue={infoEdit?.comment || undefined}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </div>

              <div className='d-flex algin-items-center justify-content-end gap-3 mt-4'>
                <Button type='button' variant='light' onClick={onClose}>
                  <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                </Button>
                <Button type='submit' variant='primary' disabled={loading}>
                  {loading && <Spinner size='sm' animation='border' />}
                  <FormattedMessage id='common.action.save' defaultMessage='Save' />
                </Button>
              </div>
            </div>
          </form>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}
